/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Ad Studio overview", description = "Ad Studio generates on-brand ad creatives from a brief, product, existing ad, competitor, or trend. Refine with the agent or a visual editor, then launch to your connected ad platforms.", lastUpdatedDate = new Date(1792368000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Ad Studio overview",
        description: "Ad Studio generates on-brand ad creatives from a brief, product, existing ad, competitor, or trend. Refine with the agent or a visual editor, then launch to your connected ad platforms.",
        lastUpdatedDate: "Mon Oct 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "What you can make in Ad Studio",
          "depth": 0,
          "id": "what-you-can-make-in-ad-studio"
        }, {
          "value": "How ads get made",
          "depth": 0,
          "id": "how-ads-get-made"
        }, {
          "value": "Open Ad Studio",
          "depth": 0,
          "id": "open-ad-studio"
        }, {
          "value": "Get started",
          "depth": 0,
          "id": "get-started"
        }, {
          "value": "Set up the workspace",
          "depth": 1,
          "id": "set-up-the-workspace"
        }, {
          "value": "Add inputs for ad generation",
          "depth": 1,
          "id": "add-inputs-for-ad-generation"
        }, {
          "value": "Create, refine, and launch ads",
          "depth": 1,
          "id": "create-refine-and-launch-ads"
        }, {
          "value": "Use Ad Studio with other Hightouch products",
          "depth": 0,
          "id": "use-ad-studio-with-other-hightouch-products"
        }, {
          "value": "Ground ads in your brand context",
          "depth": 1,
          "id": "ground-ads-in-your-brand-context"
        }, {
          "value": "Launch and hand off finished ads",
          "depth": 1,
          "id": "launch-and-hand-off-finished-ads"
        }, {
          "value": "Build audiences, measure, and analyze",
          "depth": 1,
          "id": "build-audiences-measure-and-analyze"
        }, {
          "value": "Permissions",
          "depth": 0,
          "id": "permissions"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      h4: "h4",
      a: "a",
      ol: "ol",
      li: "li",
      h3: "h3",
      ul: "ul",
      code: "code"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Audience"
            }), _jsx(_components.th, {
              children: "How you'll use this article"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Marketers"
              })
            }), _jsx(_components.td, {
              children: "Learn what Ad Studio makes and how to create, refine, and launch ads."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Data teams"
              })
            }), _jsx(_components.td, {
              children: "Learn what workspace configuration Ad Studio depends on and how it connects with the rest of Hightouch."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Ad Studio helps you create paid media creative using your brand context, products, assets, and ad performance data. Generate concepts with the agent, refine them in the chat or a visual editor, then launch approved ads to your connected ad platforms."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "what-you-can-make-in-ad-studio",
        children: "What you can make in Ad Studio"
      }), "\n", _jsx(_components.p, {
        children: "Marketers and creative teams use Ad Studio to create static image ads, motion ads, and short-form video for paid media."
      }), "\n", _jsx(_components.p, {
        children: "You can start from a campaign brief, a product in your catalog, an existing ad, a competitor ad, or a trend. The agent uses the brand context and creative inputs available in your workspace to generate concepts for you to review and refine."
      }), "\n", _jsx(_components.p, {
        children: "Ad Studio also uses performance data from connected ad platforms to help you identify fatiguing creative and turn those insights into new ads."
      }), "\n", _jsx(_components.h4, {
        id: "example-refresh-a-fatiguing-ad",
        children: "Example: Refresh a fatiguing ad"
      }), "\n", _jsxs(_components.p, {
        children: ["Your Meta prospecting campaign has run the same creative for three weeks and CTR is falling. In ", _jsx(_components.a, {
          href: "/ad-studio/insights",
          children: "Insights"
        }), ", you find the fatiguing ad and select ", _jsx(_components.strong, {
          children: "Create ad"
        }), ". The agent generates new variants, you adjust the strongest concept in the visual editor, then launch it to the campaign."]
      }), "\n", _jsx(_components.h4, {
        id: "example-respond-to-a-competitor-ad",
        children: "Example: Respond to a competitor ad"
      }), "\n", _jsxs(_components.p, {
        children: ["A competitor launches a campaign promoting a feature you also offer. You upload a screenshot, select ", _jsx(_components.strong, {
          children: "Riff on a competitor"
        }), ", and ask the agent to create your own version using your brand context, product messaging, and assets. You review and refine the concepts before launching."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "how-ads-get-made",
        children: "How ads get made"
      }), "\n", _jsx(_components.p, {
        children: "Ad creation in Ad Studio follows the same general workflow:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Start or open a chat"
          }), " for the campaign you're working on."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Choose a starting point, if needed:"
          }), " a brief, product, existing ad, competitor ad, or trend. You can also start by describing what you want."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Prompt the agent"
          }), " with what you want to create."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Review the generated concepts"
          }), " and choose which ones to keep."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Refine"
          }), " them in the chat or the visual editor."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Launch"
          }), " approved ads to an ad platform, or export them to Figma."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/ad-studio/ads",
          children: "Create ads"
        }), " for the full workflow and the ", _jsx(_components.a, {
          href: "/ad-studio/prompting-guide",
          children: "Prompting guide"
        }), " for guidance and example prompts."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "open-ad-studio",
        children: "Open Ad Studio"
      }), "\n", _jsxs(_components.p, {
        children: ["Open ", _jsx(_components.a, {
          href: "https://app.hightouch.com/ad-studio?from=docs",
          children: "Ad Studio"
        }), " in Hightouch."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "get-started",
        children: "Get started"
      }), "\n", _jsx(_components.p, {
        children: "You can create your first ad once you've connected an ad platform. Additional workspace configuration and creative inputs help the agent generate ads that better reflect your brand, products, and campaigns."
      }), "\n", _jsx(_components.h3, {
        id: "set-up-the-workspace",
        children: "Set up the workspace"
      }), "\n", _jsx(_components.p, {
        children: "Platform admins typically configure these settings once for everyone using Ad Studio."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Do this"
            }), _jsx(_components.th, {
              children: "To get"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/context-hub/connections#connect-advertising-and-lifecycle-platforms",
                children: "Connect your ad platforms"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Required."
              }), " A destination for launched ads, and performance data for Ad Studio to analyze."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/context-hub/brands",
                children: "Create a brand profile"
              })
            }), _jsx(_components.td, {
              children: "Business, product, audience, and brand context for generated ads."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/context-hub/brands",
                children: "Upload brand guidelines"
              })
            }), _jsx(_components.td, {
              children: "Colors, typography, and logo usage for the agent to follow."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/context-hub/brands#add-logo-files",
                children: "Upload logo files and fonts"
              })
            }), _jsx(_components.td, {
              children: "Your actual logos and typefaces in generated ads."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/context-hub/guardrails#set-ad-guardrails",
                children: "Set guardrails"
              })
            }), _jsx(_components.td, {
              children: "Rules for which image modifications the agent can make."
            })]
          })]
        })]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Ad Studio doesn't require a CDP deployment or warehouse connection. Workspace-level brand configuration and ad platform connections live in ", _jsx(_components.a, {
            href: "/context-hub/overview",
            children: "Context Hub"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "add-inputs-for-ad-generation",
        children: "Add inputs for ad generation"
      }), "\n", _jsx(_components.p, {
        children: "Marketers and creative teams add the content and business context the agent draws on when generating ads. These inputs aren't required to open Ad Studio, and you can add or update them as your campaigns evolve."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Do this"
            }), _jsx(_components.th, {
              children: "To get"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/context-hub/assets",
                children: "Upload assets"
              })
            }), _jsx(_components.td, {
              children: "Images, logos, and video the agent can use directly in ads."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/context-hub/assets#connect-a-product-catalog",
                children: "Configure a product catalog"
              })
            }), _jsx(_components.td, {
              children: "Product names, images, descriptions, and pricing for product-based ads."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/context-hub/business-context#add-business-context",
                children: "Add context cards"
              })
            }), _jsx(_components.td, {
              children: "Campaign formats, metric definitions, messaging guidance, and other organizational knowledge the agent can reference."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "You don't need every input for every ad. Add the sources relevant to the creative you're generating."
      }), "\n", _jsx(_components.h3, {
        id: "create-refine-and-launch-ads",
        children: "Create, refine, and launch ads"
      }), "\n", _jsx(_components.p, {
        children: "Once your workspace and inputs are ready, work through the Ad Studio workflow:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsxs(_components.strong, {
            children: [_jsx(_components.a, {
              href: "/ad-studio/ads",
              children: "Create ads"
            }), "."]
          }), " Start a chat, choose a starting point, and generate concepts. You can also create additional sizes, variants, and motion versions."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsxs(_components.strong, {
            children: [_jsx(_components.a, {
              href: "/ad-studio/refine",
              children: "Refine and collaborate"
            }), "."]
          }), " Iterate with the agent or the visual editor, and use comments to collaborate before you approve."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsxs(_components.strong, {
            children: [_jsx(_components.a, {
              href: "/ad-studio/launch",
              children: "Launch ads"
            }), "."]
          }), " Queue approved ads and send them to a connected platform, or ", _jsx(_components.a, {
            href: "/ad-studio/figma",
            children: "hand them off to Figma"
          }), " for additional design work."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/ad-studio/insights",
          children: "Insights"
        }), " also surfaces fatiguing creative and competitor activity you can use as starting points for new ads. For prompt examples across formats and workflows, see the ", _jsx(_components.a, {
          href: "/ad-studio/prompting-guide",
          children: "Prompting guide"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "use-ad-studio-with-other-hightouch-products",
        children: "Use Ad Studio with other Hightouch products"
      }), "\n", _jsx(_components.p, {
        children: "Ad Studio uses brand context and connected-channel data from your Hightouch workspace to generate ads, then connects finished creative to your launch, audience, and measurement workflows."
      }), "\n", _jsx(_components.h3, {
        id: "ground-ads-in-your-brand-context",
        children: "Ground ads in your brand context"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Context Hub"
          }), " — Brand profiles, guidelines, guardrails, and business context in ", _jsx(_components.a, {
            href: "/context-hub/overview",
            children: "Context Hub"
          }), " shape what the agent generates."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Connected ad platforms"
          }), " — Campaign performance data from the ", _jsx(_components.a, {
            href: "/context-hub/connections#connect-advertising-and-lifecycle-platforms",
            children: "channels you connect"
          }), " powers ", _jsx(_components.a, {
            href: "/ad-studio/insights",
            children: "Insights"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "launch-and-hand-off-finished-ads",
        children: "Launch and hand off finished ads"
      }), "\n", _jsxs(_components.p, {
        children: ["Launch approved ads to a connected ad platform, or ", _jsx(_components.a, {
          href: "/ad-studio/figma",
          children: "hand them off to Figma"
        }), " for additional design work."]
      }), "\n", _jsx(_components.h3, {
        id: "build-audiences-measure-and-analyze",
        children: "Build audiences, measure, and analyze"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/customer-studio/overview",
              children: "Customer Studio"
            })
          }), " — Build and sync the audiences your campaigns target."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/campaign-intelligence/overview",
              children: "Intelligence"
            })
          }), " — Measure campaign performance, then use those results to inform new creative."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/agents/overview",
              children: "Agents"
            })
          }), " — Analyze customer and campaign data in natural language, then use what you learn to guide the ads you create."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "permissions",
        children: "Permissions"
      }), "\n", _jsxs(_components.p, {
        children: ["Access to Ad Studio is controlled through custom roles. In the custom role builder, open the ", _jsx(_components.strong, {
          children: "Agents"
        }), " tab and configure the ", _jsx(_components.code, {
          children: "Access Agents"
        }), " and ", _jsx(_components.code, {
          children: "Configure Agents"
        }), " grants. A user needs at least ", _jsx(_components.code, {
          children: "Access Agents"
        }), " to open Ad Studio."]
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/workspace-management/roles#agents",
          children: "Roles"
        }), " for the full grant list, and ", _jsx(_components.a, {
          href: "/agents/overview#data-governance",
          children: "Agents data governance"
        }), " for how column- and row-level access apply to the data the agent can read."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "You approve every ad before it goes live. Ad Studio never launches an ad on its own."
        })
      })]
    });
  }
}
export default MDXContent;
